import { useEffect } from "react";

/**
 * Injects the dynamic PWA manifest URL and runtime meta tags
 * (theme-color, apple-mobile-web-app-title, apple-touch-icon)
 * by fetching the live config from the manifest-dynamic edge function.
 * Falls back silently to the static manifest if the call fails.
 */
export function usePWAManifest() {
  useEffect(() => {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manifest-dynamic`;

    // 1. Point <link rel="manifest"> to the dynamic endpoint.
    let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      document.head.appendChild(link);
    }
    link.href = url;

    // 2. Pull config to update meta tags + apple touch icon at runtime.
    let cancelled = false;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => {
        if (!m || cancelled) return;

        const setMeta = (name: string, content: string) => {
          let tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
          if (!tag) {
            tag = document.createElement("meta");
            tag.name = name;
            document.head.appendChild(tag);
          }
          tag.content = content;
        };

        if (m.theme_color) setMeta("theme-color", m.theme_color);
        if (m.short_name) setMeta("apple-mobile-web-app-title", m.short_name);

        const icon192 = m.icons?.find((i: any) => i.sizes === "192x192");
        if (icon192?.src) {
          document
            .querySelectorAll('link[rel="apple-touch-icon"]')
            .forEach((el) => el.setAttribute("href", icon192.src));
        }
      })
      .catch(() => {
        /* keep static fallback */
      });

    return () => {
      cancelled = true;
    };
  }, []);
}